import React, { useContext } from 'react';
import {
  Box,
  Heading,
  ResponsiveContext,
  colors,
  Text,
  Icon,
} from '@alinta-digital/asg';
import { FormikProps } from 'formik';
import { FuelSite } from '../../types';
import { pushAnalytics, scrollToScrollElement } from '../../utils/utils';
import { Element } from 'react-scroll';
import { DirectDebitForm } from '../Form/Form';
import { StyledTextThanks } from '../../Common.Styled';
import { ChevronRightIcon } from '../Icons/ChevronRightIcon';
import { EBillIcon } from '../Icons/EBillIcon';
import { UnAuthSuccessIcon } from '../Icons/SuccessIcon';
import styled from 'styled-components';
import { Button } from './Button';
import { emitGAMouseClickHandler } from '../../utils/analytics.helper';

function AccountText(props: { site: FuelSite }) {
  return (
    <div>
      <span
        key={props.site.authAccountNumber}
        data-testid="finaliseFuelAccounts"
        style={{
          width: '40%',
          minWidth: '306px',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: '18px',
          lineHeight: '24px',
        }}
      >
        {props.site.fuelType === 'gas' ? 'Gas' : 'Electricity'} account Number
        <br />
      </span>

      <span
        key={props.site.authAccountNumber}
        style={{
          width: '40%',
          minWidth: '306px',
          textAlign: 'center',
          fontSize: '16px',
        }}
      >
        {props.site.authAccountNumber}
      </span>
    </div>
  );
}

const SAnchor = styled.a`
  display: flex;
  text-decoration: inherit;
  color: #196cff;
  font-weight: 400;
`;

// @ts-ignore ignore
const SButton = styled(Button)`
  gap: 15px;
  flex: 1;

  svg {
    min-width: 12px;
  }
`;

const SPlainButton = styled(Button)`
  gap: 15px;
  margin-bottom: 8px;
  font-size: 16px;
  min-height: 38px;

  svg {
    height: 12px;
    min-width: 12px;
  }
`;

interface ThankYouProps<TProps extends DirectDebitForm> {
  formikProps: FormikProps<TProps>;
}

export const ThankYou = <TProps extends DirectDebitForm>({
  formikProps,
}: ThankYouProps<TProps>): any => {
  const {
    electricity: parentFormElectricity,
    gas: parentFormGas,
  } = formikProps.values.connection;

  const responsiveSize = useContext(ResponsiveContext);
  const isSmall = responsiveSize === 'xs' || responsiveSize === 'sm';
  const isMediumOrLarger = responsiveSize === 'md' || responsiveSize === 'lg';
  const myAccountBaseUrl = process.env.REACT_APP_MYACCOUNT_BASE_URL!;

  React.useEffect(() => {
    scrollToScrollElement('thankYouStart');
  });

  React.useEffect(() => {
    pushAnalytics('load.0.dd.succ');
  }, []);

  return (
    <Box width={isMediumOrLarger ? '80%' : '100%'} margin="0 auto">
      <Element name="thankYouStart" id="thankYouStart" />
      <Box
        background="#fff"
        margin={{ top: 'medium', bottom: '211px' }}
        pad={{
          top: responsiveSize === 'lg' ? 'medium' : 'small',
          horizontal: isMediumOrLarger ? 'xxlarge' : 'medium',
        }}
        style={{ borderRadius: '4px' }}
        height="auto"
      >
        {/* tick section */}
        <Box
          margin={{
            top: responsiveSize === 'lg' ? 'small' : '0px',
            bottom: '0px',
          }}
          width="100%"
        >
          <Box margin={{ horizontal: 'auto', vertical: 'small' }}>
            <Icon
              id="responseIcon"
              icon={UnAuthSuccessIcon}
              height="54px"
              width="54px"
              data-testid="responseIcon"
            />
          </Box>
        </Box>
        <Box>
          <Heading
            level="3"
            style={{
              fontSize: isSmall ? '28px' : '36px',
            }}
            margin={{ horizontal: 'auto', bottom: 'xsmall' }}
            data-testid="responseHeader"
            weight={700}
            color={'#000F20'}
          >
            Setup successful
          </Heading>
        </Box>

        <StyledTextThanks textAlign="center" color="#000F20" size="16px">
          {parentFormElectricity.map((site: FuelSite) => (
            <AccountText site={site} key={site.accountNumber} />
          ))}
          {parentFormGas.map((site: FuelSite) => (
            <AccountText site={site} key={site.accountNumber} />
          ))}
          <br />
          <span
            style={{
              textAlign: 'center',
              fontSize: isSmall ? '16px' : '16px',
              fontWeight: 400,
              maxWidth: '752px',
              minWidth: '346px',
            }}
          >
            You’ll receive a confirmation email shortly and your bank account
            will be direct debited on the due date of any future bills. Please
            note it may take up to 24 hours for the direct debit to be applied
            to your account.
          </span>

          <br />
        </StyledTextThanks>

        <Box
          displayAs="flex"
          alignContent="center"
          justify="center"
          margin={{ top: '80px', bottom: '80px' }}
        >
          <Box direction="column" maxWidth="350px" width="100%" margin="0 auto">
            <Text
              size="24px"
              weight={700}
              data-testid="setAndForgetTitle"
              margin={{ bottom: '10px' }}
              color={'#000F20'}
              style={{ lineHeight: '32px' }}
            >
              Manage your direct debits
            </Text>
            <Text
              size="16px"
              color={colors.text.light}
              margin={{ bottom: '15px' }}
              style={{ fontWeight: 400, lineHeight: '22px' }}
              data-testid="responseText"
            >
              To view your account balance, bills and payment history, visit
              MyAccount:
            </Text>

            <SAnchor href={`${myAccountBaseUrl}/login`} target="_blank">
              <SButton
                primary
                data-testid="myAccountButton"
                data-ga="click.1.dd.malogin"
                onMouseDown={emitGAMouseClickHandler}
              >
                <Icon
                  id="unauthbuttonIcon"
                  data-testid="unauthbuttonIcon"
                  icon={EBillIcon}
                  height="12px"
                ></Icon>{' '}
                Login to MyAccount
              </SButton>
            </SAnchor>
          </Box>
          <Box
            maxWidth="350px"
            width="100%"
            height="fit-content"
            margin={{ horizontal: 'auto', bottom: 'small', top: '48px' }}
            direction="column"
            align="center"
          >
            <Box direction="column" width="100%" margin={{ bottom: '12px' }}>
              <Text
                size="20px"
                textAlign="center"
                weight={700}
                data-testid="OtherThingsText"
                color={'#000F20'}
              >
                Other things you can do in MyAccount
              </Text>
            </Box>
            <Box direction="column" width="100%" style={{ display: 'flex' }}>
              <SAnchor
                href={`${myAccountBaseUrl}/usage?deepLink=true`}
                target="_blank"
              >
                <SPlainButton
                  plain
                  data-ga="click.2.dd.vu"
                  onMouseDown={emitGAMouseClickHandler}
                >
                  View your usage{' '}
                  <Icon id="LinkUsage" icon={ChevronRightIcon} />
                </SPlainButton>
              </SAnchor>
              <SAnchor
                href={`${myAccountBaseUrl}/payments?deepLink=true`}
                target="_blank"
              >
                <SPlainButton
                  plain
                  data-ga="click.3.dd.vbh"
                  onMouseDown={emitGAMouseClickHandler}
                >
                  View your payment history{' '}
                  <Icon id="LinkUsage" icon={ChevronRightIcon} />
                </SPlainButton>
              </SAnchor>

              <Box
                direction="column"
                width="100%"
                align="center"
                margin={{ top: '16px' }}
              >
                <SAnchor
                  href={
                    'https://www.alintaenergy.com.au/residential/billing-and-pricing/your-online-account'
                  }
                >
                  <SButton
                    data-testid="MyAccountMoreFeature"
                    secondary
                    style={{ minWidth: '165px', gap: '10px' }}
                    data-ga="click.4.dd.vmma"
                    onMouseDown={emitGAMouseClickHandler}
                  >
                    See more MyAccount features
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.175 9H0V7H12.175L6.575 1.4L8 0L16 8L8 16L6.575 14.6L12.175 9Z"
                        fill="#4387FF"
                      />
                    </svg>
                  </SButton>
                </SAnchor>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
